import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSharedLists,
  createSharedList,
  deleteSharedList,
  inviteSharedListMember,
  removeSharedListMember,
  updateSharedList,
} from '../api/shared-lists';
import type {
  SharedListCreateInput,
  SharedListMemberInviteInput,
  SharedListPermission,
} from '@danny-tasks/shared';

export function useSharedLists() {
  const queryClient = useQueryClient();

  const { data: lists = [], isLoading } = useQuery({
    queryKey: ['shared-lists'],
    queryFn: listSharedLists,
  });

  const create = useMutation({
    mutationFn: (input: SharedListCreateInput) => createSharedList(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-lists'] }),
  });

  const remove = useMutation({
    mutationFn: (listId: string) => deleteSharedList(listId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-lists'] }),
  });

  const update = useMutation({
    mutationFn: ({
      listId,
      patch,
    }: {
      listId: string;
      patch: { name?: string; linkPermission?: SharedListPermission | null };
    }) => updateSharedList(listId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-lists'] }),
  });

  const inviteMember = useMutation({
    mutationFn: ({ listId, input }: { listId: string; input: SharedListMemberInviteInput }) =>
      inviteSharedListMember(listId, input),
    onSuccess: (_data, { listId }) =>
      queryClient.invalidateQueries({ queryKey: ['shared-list', listId] }),
  });

  const removeMember = useMutation({
    mutationFn: ({ listId, memberId }: { listId: string; memberId: string }) =>
      removeSharedListMember(listId, memberId),
    onSuccess: (_data, { listId }) =>
      queryClient.invalidateQueries({ queryKey: ['shared-list', listId] }),
  });

  return { lists, isLoading, create, remove, update, inviteMember, removeMember };
}
